import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  asList,
  createBank,
  deleteBank,
  duplicateBank,
  listBanks,
  setBankStatus,
  updateBank,
} from "./api";
import {
  ActionMenu,
  ConfirmDialog,
  CounterPill,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  StatusBadge,
} from "./components/Ui";
import { PAGE_SIZE_OPTIONS, STATUS, STATUS_OPTIONS } from "./constants";
import { PERMISSIONS, can } from "./permissions";
import { useApiCall } from "./useApiCall";
import { validateBank } from "./validation";
import "./questionData.css";

const EMPTY_FORM = {
  question_bank_id: "",
  name: "",
  description: "",
  status: STATUS.ACTIVE,
};

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "questions_desc", label: "Most questions" },
];

const num = (v) => Number(v || 0);

const QuestionBanksPage = () => {
  document.title = "Question Bank | Medicotoon";
  const navigate = useNavigate();
  const call = useApiCall();

  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Search / filter / sort / paginate
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Create / edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Duplicate modal
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { ok, data, message } = await call(() => listBanks({}), {
      silent: true,
    });
    if (ok) {
      setBanks(asList(data).rows);
    } else {
      setLoadError(message || "Failed to load question banks");
      setBanks([]);
    }
    setLoading(false);
  }, [call]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  /* ---------------------------------------------------------------- */
  /* Derived list                                                      */
  /* ---------------------------------------------------------------- */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = banks.filter((b) => {
      const matchesTerm =
        !term ||
        String(b.name || "").toLowerCase().includes(term) ||
        String(b.description || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" || String(b.status) === statusFilter;
      return matchesTerm && matchesStatus;
    });

    rows = [...rows].sort((a, b) => {
      switch (sortBy) {
        case "name_desc":
          return String(b.name || "").localeCompare(String(a.name || ""));
        case "newest":
          return String(b.created_at || "").localeCompare(String(a.created_at || ""));
        case "oldest":
          return String(a.created_at || "").localeCompare(String(b.created_at || ""));
        case "questions_desc":
          return num(b.questions_count) - num(a.questions_count);
        case "name_asc":
        default:
          return String(a.name || "").localeCompare(String(b.name || ""));
      }
    });

    return rows;
  }, [banks, search, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortBy, pageSize]);

  const totals = useMemo(
    () => ({
      banks: banks.length,
      units: banks.reduce((s, b) => s + num(b.units_count), 0),
      lectures: banks.reduce((s, b) => s + num(b.lectures_count), 0),
      questions: banks.reduce((s, b) => s + num(b.questions_count), 0),
    }),
    [banks]
  );

  /* ---------------------------------------------------------------- */
  /* Create / edit                                                     */
  /* ---------------------------------------------------------------- */
  const openCreate = () => {
    setModalMode("add");
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (bank) => {
    setModalMode("edit");
    setForm({
      question_bank_id: bank.question_bank_id,
      name: bank.name || "",
      description: bank.description || "",
      status: String(bank.status) === STATUS.INACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE,
    });
    setErrors({});
    setModalOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const validation = validateBank(form);
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || "",
      status: form.status,
    };

    const { ok } =
      modalMode === "add"
        ? await call(() => createBank(payload), {
            success: "Question bank created",
          })
        : await call(
            // question_bank_id is the stable identity - renaming never changes it,
            // so existing course associations survive an edit.
            () => updateBank({ ...payload, question_bank_id: form.question_bank_id }),
            { success: "Question bank updated" }
          );

    setSaving(false);
    if (ok) {
      setModalOpen(false);
      fetchBanks();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Row actions                                                       */
  /* ---------------------------------------------------------------- */
  const toggleStatus = async (bank) => {
    const next =
      String(bank.status) === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE;
    const { ok } = await call(() => setBankStatus(bank.question_bank_id, next), {
      success: next === STATUS.ACTIVE ? "Bank activated" : "Bank deactivated",
    });
    if (ok) fetchBanks();
  };

  const openDuplicate = (bank) => {
    setDuplicateTarget(bank);
    setDuplicateName(`${bank.name} (Copy)`);
  };

  const confirmDuplicate = async () => {
    if (!duplicateName.trim()) return;
    setDuplicating(true);
    const { ok } = await call(
      () => duplicateBank(duplicateTarget.question_bank_id, duplicateName.trim()),
      { success: "Question bank duplicated" }
    );
    setDuplicating(false);
    if (ok) {
      setDuplicateTarget(null);
      fetchBanks();
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    const { ok } = await call(() => deleteBank(deleteTarget.question_bank_id), {
      success: "Question bank deleted",
    });
    setDeleting(false);
    if (ok) {
      setDeleteTarget(null);
      fetchBanks();
    }
  };

  const openBank = (bank) =>
    navigate(`/question-data/banks/${bank.question_bank_id}`);

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  if (!can(PERMISSIONS.BANK_VIEW)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Question Bank" />
          <EmptyState
            icon="bx bx-lock-alt"
            title="Access denied"
            description="You do not have permission to view question banks."
          />
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Question Bank" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="qd-toolbar">
                    <div className="qd-toolbar-stats">
                      <CounterPill
                        icon="bx bx-library"
                        count={totals.banks}
                        label="banks"
                      />
                      <CounterPill
                        icon="bx bx-folder"
                        count={totals.units}
                        label="units"
                      />
                      <CounterPill
                        icon="bx bx-book-open"
                        count={totals.lectures}
                        label="lectures"
                      />
                      <CounterPill
                        icon="bx bx-help-circle"
                        count={totals.questions}
                        label="questions"
                      />
                    </div>

                    <div className="qd-toolbar-actions">
                      <Button
                        color="light"
                        onClick={() => navigate("/question-data/course-assignments")}
                      >
                        <i className="bx bx-link me-1" />
                        Course Associations
                      </Button>
                      {can(PERMISSIONS.BANK_CREATE) ? (
                        <Button color="primary" onClick={openCreate}>
                          <i className="bx bx-plus me-1" />
                          Create Question Bank
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  <Row className="g-2 mt-3">
                    <Col md={5}>
                      <div className="search-box position-relative">
                        <Input
                          type="text"
                          placeholder="Search banks by name or description..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All statuses</option>
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        {SORT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n} / page
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    {loading ? (
                      <LoadingState label="Loading question banks..." />
                    ) : loadError ? (
                      <ErrorState message={loadError} onRetry={fetchBanks} />
                    ) : !banks.length ? (
                      <EmptyState
                        icon="bx bx-library"
                        title="No question banks yet"
                        description="Create your first question bank to start organising units, lectures and questions."
                        action={
                          can(PERMISSIONS.BANK_CREATE) ? (
                            <Button color="primary" onClick={openCreate}>
                              <i className="bx bx-plus me-1" />
                              Create Question Bank
                            </Button>
                          ) : null
                        }
                      />
                    ) : !filtered.length ? (
                      <EmptyState
                        icon="bx bx-search"
                        title="No matching banks"
                        description="Try a different search term or clear the filters."
                        action={
                          <Button
                            color="light"
                            onClick={() => {
                              setSearch("");
                              setStatusFilter("all");
                            }}
                          >
                            Clear filters
                          </Button>
                        }
                      />
                    ) : (
                      <Row className="g-3">
                        {pageRows.map((bank) => (
                          <Col key={bank.question_bank_id} md={6} xl={4}>
                            <div
                              className="qd-bank-card"
                              role="button"
                              tabIndex={0}
                              onClick={() => openBank(bank)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") openBank(bank);
                              }}
                            >
                              <div className="qd-bank-card-head">
                                <div className="qd-bank-title">
                                  <h5 className="mb-1 text-truncate" title={bank.name}>
                                    {bank.name}
                                  </h5>
                                  <StatusBadge status={bank.status} />
                                </div>
                                <ActionMenu
                                  actions={[
                                    {
                                      key: "view",
                                      label: "View",
                                      icon: "bx bx-show",
                                      onClick: () => openBank(bank),
                                    },
                                    {
                                      key: "edit",
                                      label: "Edit",
                                      icon: "bx bx-edit",
                                      hidden: !can(PERMISSIONS.BANK_UPDATE),
                                      onClick: () => openEdit(bank),
                                    },
                                    {
                                      key: "status",
                                      label:
                                        String(bank.status) === STATUS.ACTIVE
                                          ? "Deactivate"
                                          : "Activate",
                                      icon: "bx bx-toggle-left",
                                      hidden: !can(PERMISSIONS.BANK_UPDATE),
                                      onClick: () => toggleStatus(bank),
                                    },
                                    {
                                      key: "duplicate",
                                      label: "Duplicate",
                                      icon: "bx bx-copy",
                                      hidden: !can(PERMISSIONS.BANK_CREATE),
                                      onClick: () => openDuplicate(bank),
                                    },
                                    { key: "d1", divider: true },
                                    {
                                      key: "delete",
                                      label: "Delete",
                                      icon: "bx bx-trash",
                                      danger: true,
                                      hidden: !can(PERMISSIONS.BANK_DELETE),
                                      onClick: () => setDeleteTarget(bank),
                                    },
                                  ]}
                                />
                              </div>

                              <p className="qd-bank-desc">
                                {bank.description || (
                                  <span className="text-muted fst-italic">
                                    No description
                                  </span>
                                )}
                              </p>

                              <div className="qd-bank-counters">
                                <CounterPill
                                  icon="bx bx-folder"
                                  count={num(bank.units_count)}
                                  label="units"
                                />
                                <CounterPill
                                  icon="bx bx-book-open"
                                  count={num(bank.lectures_count)}
                                  label="lectures"
                                />
                                <CounterPill
                                  icon="bx bx-help-circle"
                                  count={num(bank.questions_count)}
                                  label="questions"
                                />
                              </div>

                              <div className="qd-bank-id text-muted">
                                ID: {bank.question_bank_id}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>

                  {!loading && filtered.length > pageSize ? (
                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                      <span className="text-muted small">
                        Showing {(currentPage - 1) * pageSize + 1}-
                        {Math.min(currentPage * pageSize, filtered.length)} of{" "}
                        {filtered.length}
                      </span>
                      <Pagination className="mb-0">
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => setPage(currentPage - 1)}
                          />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                          (p) => (
                            <PaginationItem key={p} active={p === currentPage}>
                              <PaginationLink onClick={() => setPage(p)}>
                                {p}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        )}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink
                            next
                            onClick={() => setPage(currentPage + 1)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create / edit bank */}
      <Modal isOpen={modalOpen} toggle={() => !saving && setModalOpen(false)} centered>
        <ModalHeader toggle={() => !saving && setModalOpen(false)}>
          {modalMode === "add" ? "Create Question Bank" : "Edit Question Bank"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={submitForm}>
            <Field label="Bank name" required error={errors.name}>
              <Input
                type="text"
                value={form.name}
                autoFocus
                placeholder="e.g. Medicine Question Bank"
                invalid={!!errors.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>

            <Field
              label="Description"
              error={errors.description}
              hint="Optional"
            >
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </Field>

            <Field label="Status">
              <Input
                type="select"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </Field>

            {modalMode === "edit" ? (
              <div className="alert alert-info py-2 small">
                Bank ID <b>{form.question_bank_id}</b> never changes - renaming
                keeps every course association intact.
              </div>
            ) : null}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                type="button"
                color="light"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-1" /> : null}
                {modalMode === "add" ? "Create" : "Save changes"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Duplicate bank */}
      <Modal
        isOpen={!!duplicateTarget}
        toggle={() => !duplicating && setDuplicateTarget(null)}
        centered
      >
        <ModalHeader toggle={() => !duplicating && setDuplicateTarget(null)}>
          Duplicate Question Bank
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            A full copy of all units, lectures and questions will be created.
            Course associations are not copied.
          </p>
          <Field label="New bank name" required>
            <Input
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
            />
          </Field>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              color="light"
              onClick={() => setDuplicateTarget(null)}
              disabled={duplicating}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={confirmDuplicate}
              disabled={duplicating || !duplicateName.trim()}
            >
              {duplicating ? <Spinner size="sm" className="me-1" /> : null}
              Duplicate
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Delete bank */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete question bank"
        message={`Delete "${deleteTarget?.name}"? Every unit, lecture and question inside it will be removed.`}
        warning={
          num(deleteTarget?.courses_count) > 0
            ? `This bank is currently attached to ${num(
                deleteTarget?.courses_count
              )} course(s). Those courses will lose access to its questions.`
            : undefined
        }
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default QuestionBanksPage;
